import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import {
  creditApi,
  CreditTransaction,
  CreditTransactionType,
  CreditAccountStatus,
} from '../../services/api';

const RECENT_TRANSACTIONS_SHOWN = 5;

const formatAmount = (value: number): string => `${value.toFixed(2)} DZD`;

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString();

const transactionLabel = (type: CreditTransactionType): string => {
  if (type === 'CHARGE') return 'Purchase on credit';
  if (type === 'PAYMENT') return 'Repayment';
  return 'Adjustment';
};

// The ledger sign lives in the type, not in the stored amount: a CHARGE adds debt while a PAYMENT
// or an ADJUSTMENT subtracts it (a signed adjustment can therefore add debt back with a negative).
const debtDelta = (transaction: CreditTransaction): number =>
  transaction.type === 'CHARGE' ? transaction.amount : -transaction.amount;

const statusColor = (status: CreditAccountStatus): 'success' | 'warning' | 'default' => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'SUSPENDED') return 'warning';
  return 'default';
};

export const CreditBalancePanel: React.FC = () => {
  const { user } = useAuthStore();

  const { data: credit, isLoading, isError } = useQuery({
    queryKey: ['credit', 'me'],
    queryFn: () => creditApi.getMyCredit(),
    enabled: !!user,
    // A customer whose token carries no organization gets a 403 here; that is a normal state for
    // this panel, not a fault worth retrying.
    retry: false,
  });

  if (!user) {
    return null;
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  // A failed or forbidden fetch must never take the account page down with it.
  if (isError || !credit) {
    return null;
  }

  const hasArrangement = credit.creditLimit > 0 || credit.balance > 0;
  const available = Math.max(credit.creditLimit - credit.balance, 0);
  const recentTransactions = credit.transactions?.slice(0, RECENT_TRANSACTIONS_SHOWN) ?? [];

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <AccountBalanceWalletOutlinedIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Store Credit</Typography>
          {hasArrangement && (
            <Chip
              label={credit.status}
              size="small"
              color={statusColor(credit.status)}
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>

        {!hasArrangement ? (
          <Alert severity="info">
            You have no credit arrangement with this store. Ask in store to open one.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" paragraph>
              Your credit account is managed in store. Repayments are recorded by staff at the
              counter.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Outstanding balance
                </Typography>
                <Typography variant="h6">{formatAmount(credit.balance)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Credit limit
                </Typography>
                <Typography variant="h6">{formatAmount(credit.creditLimit)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Available credit
                </Typography>
                <Typography variant="h6">{formatAmount(available)}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Recent activity
            </Typography>

            {recentTransactions.length > 0 ? (
              <List dense>
                {recentTransactions.map((transaction) => {
                  const delta = debtDelta(transaction);
                  return (
                    <ListItem key={transaction.id} divider disableGutters>
                      <ListItemText
                        primary={transactionLabel(transaction.type)}
                        secondary={formatDate(transaction.createdAt)}
                      />
                      <Typography variant="body2">
                        {delta >= 0 ? '+' : '-'}
                        {formatAmount(Math.abs(delta))}
                      </Typography>
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No credit activity yet
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
